import { validate as validateEmail } from "email-validator"
import { FieldValidator, FormRecords } from "./types"

function composeValidators(...validators: Array<FieldValidator>): FieldValidator {
  return <FieldValue>(value: FieldValue) =>
    validators.reduce((error: string | undefined, validator) => error || validator(value), undefined)
}

function required(message: string = `This field is required`): FieldValidator {
  return <FieldValue>(value: FieldValue) => {
    if (value === undefined || value === null || (typeof value === `string` && value === ``)) {
      return message
    }
    return undefined
  }
}

function email(message: string = `Please enter a valid email`): FieldValidator {
  return <FieldValue>(value: FieldValue) => {
    if (value === undefined) return undefined
    if (typeof value !== `string` || !validateEmail(value)) {
      return message
    }
    return undefined
  }
}

function password(message: string = `Password needs to be at least 8 characters`): FieldValidator {
  return <FieldValue>(value: FieldValue) => {
    if (value === undefined) return undefined
    if (typeof value !== `string` || value.length < 8) {
      return message
    }
    return undefined
  }
}

function number(message: string = `Input must be a number`): FieldValidator {
  return <FieldValue>(value: FieldValue) => {
    if (value === undefined) return undefined
    if (typeof value !== `number`) {
      return message
    }
    return undefined
  }
}

function externalCondition(condition: boolean, message: string = `Input did not pass validation`): FieldValidator {
  return () => (condition === true ? undefined : message)
}

function date(message: string = `Input must be of dd/mm/yyyy`): FieldValidator {
  return <FieldValue>(value: FieldValue) => {
    if (value === undefined) return undefined

    const DATE_REGEXP =
      /(^(((0[1-9]|1[0-9]|2[0-8])[\/](0[1-9]|1[012]))|((29|30|31)[\/](0[13578]|1[02]))|((29|30)[\/](0[4,6,9]|11)))[\/](19|[2-9][0-9])\d\d$)|(^29[\/]02[\/](19|[2-9][0-9])(00|04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96)$)/ // eslint-disable-line no-useless-escape
    if (typeof value !== `string` || DATE_REGEXP.test(value) === false) return message
    return undefined
  }
}

function hasErrors(values: FormRecords): FormRecords | undefined {
  const records: FormRecords = {}

  Object.entries(values).forEach(([key, value]) => {
    if (value.validator) {
      const error = value.validator(value.value)

      if (error !== undefined) {
        records[key] = value
      }
    }
  })

  if (!Object.keys(records).length) return undefined
  return records
}

export function useFormValidators() {
  return {
    composeValidators,
    externalCondition,
    required,
    email,
    password,
    number,
    date,
    hasErrors,
  }
}
